import classnames from 'classnames';
import Image from 'next/image';
import React, { SyntheticEvent } from 'react';

import Tooltip, { TooltipDirection } from '../tooltip/Tooltip';

export type ActionIconProps = {
    className?: string;
    icon: React.JSX.Element | string;
    tooltipText?: string;
    tooltipDirection?: TooltipDirection;
    action: (event: SyntheticEvent) => void;
    disabled?: boolean;
};

export function ActionIcon(props: ActionIconProps): React.JSX.Element {
    const { icon, action, disabled } = props;
    const iconWithAction = (
        <button
            aria-label={props.tooltipText ?? 'Action Icon'}
            className={classnames(
                'relative flex w-24 h-24 items-center clickable-element rounded-4',
                'outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-cyan',
                {
                    'opacity-50 cursor-default': disabled
                }
            )}
            // A real disabled button, not just a dimmed one: without this the icon stays
            // tab-focusable and Enter-activatable while it looks inert, and callers were
            // relying on their own guards to catch the activation (CIN-14887).
            disabled={disabled}
            aria-disabled={disabled}
            onClick={disabled ? undefined : action}>
            {typeof icon === 'string' ? (
                <Image
                    className="w-full h-full object-cover"
                    src={icon}
                    width={100}
                    height={100}
                    alt="Action Icon"
                />
            ) : React.isValidElement(icon) ? (
                icon
            ) : null}
        </button>
    );
    return (
        <div className={classnames(props.className, 'relative flex items-center')}>
            {props.tooltipText ? (
                <Tooltip
                    tooltipText={props.tooltipText || ''}
                    direction={props.tooltipDirection || TooltipDirection.Top}
                    usePortal>
                    {iconWithAction}
                </Tooltip>
            ) : (
                iconWithAction
            )}
        </div>
    );
}
